import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import { getSessions, createSession, deleteSession, updateSession, getSessionParticipants } from '../utils/api';
import { useWindowSize } from '../utils/useWindowSize';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const Sessions = () => {
  const { isMobile } = useWindowSize();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [detailSession, setDetailSession] = useState(null);
  const [detailTab, setDetailTab] = useState('registered');
  const [participants, setParticipants] = useState({ enrolled: [], eligible_not_enrolled: [] });
  const [loadingParticipants, setLoadingParticipants] = useState(false);

  const emptyForm = {
    title: '',
    description: '',
    type: 'refresher_class',
    date: '',
    startTime: '',
    endTime: '',
    venue: 'Pranic Wellness Centre, Perungudi, Chennai',
    maxParticipants: '',
    openToAll: false,
    eligibleCourses: [],
    isRecurring: false,
    recurrenceType: 'none',
    recurrenceDates: [],
    recurrenceEndDate: '',
    recurrenceDays: [],
    recurrenceDayOfMonth: ''
  };

  const [form, setForm] = useState(emptyForm);

  const courseList = [
    'Basic Pranic Healing', 'Advanced Pranic Healing',
    'Pranic Psychotherapy', 'Crystal Healing',
    'Pranic Crystal Healing', 'Superbrain Yoga',
    'Arhatic Yoga Preparatory', 'Achieving Oneness with Higher Soul',
    'Kriyashakti', 'Om Mani Padme Hum',
    'Meditations on the Soul', 'Higher Clairvoyance',
    'Pranic Feng Shui', 'Manifestation and Abundance',
    'Prosperity Meditation'
  ];

  const sessionTypes = [
    { value: 'refresher_class', label: 'Refresher Class' },
    { value: 'nurturing_session', label: 'Nurturing Session' },
    { value: 'full_moon_event', label: 'Full Moon Event' },
    { value: 'workshop', label: 'Workshop' },
    { value: 'group_healing', label: 'Group Healing' },
    { value: 'special_event', label: 'Special Event' }
  ];

  const fetchSessions = useCallback(async () => {
    try {
      const res = await getSessions();
      setSessions(res.data.sessions);
    } catch (error) {
      console.log('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSession) {
        await updateSession(editingSession._id, form);
        alert('Session updated successfully');
      } else {
        const res = await createSession(form);
        alert(res.data.message);
      }
      setShowForm(false);
      setEditingSession(null);
      setForm(emptyForm);
      fetchSessions();
    } catch (error) {
      alert('Error: ' + error.response?.data?.message);
    }
  };

  const handleEdit = (session) => {
    setEditingSession(session);
    setForm({
      title: session.title,
      description: session.description,
      type: session.type,
      date: session.date?.split('T')[0],
      startTime: session.startTime,
      endTime: session.endTime,
      venue: session.venue,
      maxParticipants: session.maxParticipants,
      openToAll: session.openToAll,
      eligibleCourses: session.eligibleCourses || [],
      isRecurring: session.isRecurring || false,
      recurrenceType: session.recurrenceType || 'none',
      recurrenceDates: session.recurrenceDates || [],
      recurrenceEndDate: session.recurrenceEndDate ? session.recurrenceEndDate.split('T')[0] : '',
      recurrenceDays: session.recurrenceDays || [],
      recurrenceDayOfMonth: session.recurrenceDayOfMonth || ''
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this session?')) {
      try {
        await deleteSession(id);
        fetchSessions();
      } catch (error) {
        alert('Error deleting session');
      }
    }
  };

  const handleSendAnnouncement = async (sessionId, sessionTitle) => {
    if (!window.confirm(`Send announcement email for "${sessionTitle}" to all eligible students?`)) return;
    setSendingEmail(sessionId);
    try {
      const token = localStorage.getItem('pwc_token');
      const res = await axios.post(
        `${API_URL}/sessions/${sessionId}/send-announcement`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`Success! ${res.data.message}`);
      fetchSessions();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.message || 'No eligible students found'));
    } finally {
      setSendingEmail(null);
    }
  };

  const toggleCourse = (course) => {
    setForm(prev => ({
      ...prev,
      eligibleCourses: prev.eligibleCourses.includes(course)
        ? prev.eligibleCourses.filter(c => c !== course)
        : [...prev.eligibleCourses, course]
    }));
  };

  const addRecurrenceDate = () => {
    setForm(prev => ({ ...prev, recurrenceDates: [...prev.recurrenceDates, ''] }));
  };

  const updateRecurrenceDate = (index, value) => {
    const updated = [...form.recurrenceDates];
    updated[index] = value;
    setForm(prev => ({ ...prev, recurrenceDates: updated }));
  };

  const removeRecurrenceDate = (index) => {
    setForm(prev => ({
      ...prev,
      recurrenceDates: prev.recurrenceDates.filter((_, i) => i !== index)
    }));
  };

  const openDetail = async (session) => {
    setDetailSession(session);
    setDetailTab('registered');
    setLoadingParticipants(true);
    try {
      const res = await getSessionParticipants(session._id);
      setParticipants(res.data);
    } catch (error) {
      console.log('Error fetching participants:', error);
      setParticipants({ enrolled: [], eligible_not_enrolled: [] });
    } finally {
      setLoadingParticipants(false);
    }
  };

  const closeDetail = () => {
    setDetailSession(null);
    setParticipants({ enrolled: [], eligible_not_enrolled: [] });
  };

  const buildWhatsAppLink = (student, session) => {
    const name = `${student.firstName} ${student.lastName}`;
    const dateStr = new Date(session.date).toLocaleDateString('en-IN', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });
    const msg = `Hello ${name}, you are eligible for ${session.title} on ${dateStr} at ${session.startTime}. You have not registered yet. Please enroll at your earliest convenience. — Pranic Wellness Centre`;
    const phone = (student.phone || '').replace(/[^0-9]/g, '');
    return `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const handleRecurrenceChange = (value) => {
    if (value === 'none') {
      setForm(prev => ({ ...prev, isRecurring: false, recurrenceType: 'none', recurrenceDates: [], recurrenceEndDate: '', recurrenceDays: [], recurrenceDayOfMonth: '' }));
    } else {
      setForm(prev => ({ ...prev, isRecurring: true, recurrenceType: value, recurrenceDates: [], recurrenceDays: [], recurrenceDayOfMonth: '' }));
    }
  };

  const toggleRecurrenceDay = (day) => {
    setForm(prev => ({
      ...prev,
      recurrenceDays: prev.recurrenceDays.includes(day)
        ? prev.recurrenceDays.filter(d => d !== day)
        : [...prev.recurrenceDays, day]
    }));
  };

  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  var s = Object.assign({}, styles, isMobile ? {
    header: { ...styles.header, flexDirection: 'column', gap: '16px' },
    formCard: { ...styles.formCard, padding: '20px 16px' },
    row2: { ...styles.row2, display: 'grid', gridTemplateColumns: '1fr' },
    row4: { ...styles.row4, display: 'grid', gridTemplateColumns: '1fr 1fr' },
    recurrenceOptionsRow: { ...styles.recurrenceOptionsRow, flexDirection: 'column' },
    chipGrid: { ...styles.chipGrid, display: 'grid', gridTemplateColumns: '1fr 1fr' },
    cardGrid: { ...styles.cardGrid, gridTemplateColumns: '1fr' },
    modalOverlay: { ...styles.modalOverlay, alignItems: 'flex-end', padding: '0' },
    modalPanel: { ...styles.modalPanel, borderRadius: '16px 16px 0 0', maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto' },
    formBtns: { ...styles.formBtns, flexDirection: 'column' },
    submitBtn: { ...styles.submitBtn, width: '100%' },
    cancelBtn: { ...styles.cancelBtn, width: '100%' },
    addBtn: { ...styles.addBtn, width: '100%' }
  } : {});

  return (
    <div style={styles.layout}>
      <Sidebar />
      <div style={{ ...styles.main, marginLeft: isMobile ? 0 : '260px', padding: isMobile ? '56px 16px 16px' : '32px' }}>

        {/* Header */}
        <div style={s.header}>
          <div>
            <h1 style={styles.headerTitle}>Sessions & Events</h1>
            <p style={styles.headerSub}>Create and manage classes, nurturing sessions and events</p>
          </div>
          <button
            onClick={() => {
              setEditingSession(null);
              setForm(emptyForm);
              setShowForm(!showForm);
            }}
            style={s.addBtn}
          >
            {showForm ? 'Cancel' : '+ Create New Session'}
          </button>
        </div>

        {/* Create / Edit Session Form — Compact Corporate Design */}
        {showForm && (
          <div style={s.formCard}>
            <div style={styles.formTitle}>
              {editingSession ? `Edit Session — ${editingSession.title}` : 'Create New Session'}
            </div>
            <form onSubmit={handleSubmit}>
              {/* Row 1: Title + Type */}
              <div style={s.row2}>
                <div style={styles.fieldFlex2}>
                  <label style={styles.label}>Session Title</label>
                  <input
                    style={styles.inputCompact}
                    value={form.title}
                    onChange={e => setForm({ ...form, title: e.target.value })}
                    placeholder="e.g. Basic Pranic Healing Refresher"
                    required
                  />
                </div>
                <div style={styles.fieldFlex1}>
                  <label style={styles.label}>Type</label>
                  <select
                    style={styles.inputCompact}
                    value={form.type}
                    onChange={e => setForm({ ...form, type: e.target.value })}
                  >
                    {sessionTypes.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 2: Date + Start + End + Recurrence (Google Meet style) */}
              <div style={s.row4}>
                <div style={styles.fieldFlex1}>
                  <label style={styles.label}>Date</label>
                  <input
                    style={styles.inputCompact}
                    type="date"
                    value={form.date}
                    onChange={e => setForm({ ...form, date: e.target.value })}
                    required
                  />
                </div>
                <div style={styles.fieldFlexTime}>
                  <label style={styles.label}>Start</label>
                  <input
                    style={styles.inputCompact}
                    type="time"
                    value={form.startTime}
                    onChange={e => setForm({ ...form, startTime: e.target.value })}
                    required
                  />
                </div>
                <div style={styles.fieldFlexTime}>
                  <label style={styles.label}>End</label>
                  <input
                    style={styles.inputCompact}
                    type="time"
                    value={form.endTime}
                    onChange={e => setForm({ ...form, endTime: e.target.value })}
                    required
                  />
                </div>
                <div style={styles.fieldFlex1}>
                  <label style={styles.label}>Recurrence</label>
                  <select
                    style={styles.inputCompact}
                    value={form.isRecurring ? form.recurrenceType : 'none'}
                    onChange={e => handleRecurrenceChange(e.target.value)}
                  >
                    <option value="none">Does not repeat</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="custom">Custom Dates</option>
                  </select>
                </div>
              </div>

              {/* Recurrence options */}
              {form.isRecurring && (
                <div style={s.recurrenceOptionsRow}>
                  {/* Weekly — day picker */}
                  {form.recurrenceType === 'weekly' && (
                    <div style={{ flex: 1 }}>
                      <label style={styles.label}>Repeat on</label>
                      <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
                        {weekDays.map(function(day) {
                          var isActive = form.recurrenceDays.includes(day);
                          return (
                            <button key={day} type="button" onClick={function() { toggleRecurrenceDay(day); }}
                              style={{
                                width: '38px', height: '38px', borderRadius: '50%', border: isActive ? '2px solid #4a8f82' : '1px solid #e0e0e0',
                                background: isActive ? '#4a8f82' : '#fff', color: isActive ? '#fff' : '#666',
                                fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', cursor: 'pointer'
                              }}>
                              {day}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Monthly — day of month */}
                  {form.recurrenceType === 'monthly' && (
                    <div style={{ flex: 1 }}>
                      <label style={styles.label}>Repeat on day</label>
                      <select style={{ ...styles.inputCompact, maxWidth: '180px' }}
                        value={form.recurrenceDayOfMonth}
                        onChange={function(e) { setForm(function(prev) { return Object.assign({}, prev, { recurrenceDayOfMonth: e.target.value }); }); }}>
                        <option value="">Same as start date</option>
                        {Array.from({ length: 31 }, function(_, i) { return i + 1; }).map(function(d) {
                          var suffix = d === 1 || d === 21 || d === 31 ? 'st' : d === 2 || d === 22 ? 'nd' : d === 3 || d === 23 ? 'rd' : 'th';
                          return <option key={d} value={d}>{d}{suffix}</option>;
                        })}
                      </select>
                    </div>
                  )}

                  {/* Daily — no extra options needed */}
                  {form.recurrenceType === 'daily' && (
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: '13px', color: '#666' }}>Repeats every day until end date</span>
                    </div>
                  )}

                  {/* Custom Dates */}
                  {form.recurrenceType === 'custom' && (
                    <div style={{ flex: 1 }}>
                      <label style={styles.label}>Additional Dates</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                        {form.recurrenceDates.map(function(date, index) {
                          return (
                            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <input type="date" style={{ ...styles.inputCompact, width: '150px' }}
                                value={date} onChange={function(e) { updateRecurrenceDate(index, e.target.value); }} />
                              <button type="button" onClick={function() { removeRecurrenceDate(index); }} style={styles.removeSmall}>x</button>
                            </div>
                          );
                        })}
                        <button type="button" onClick={addRecurrenceDate} style={styles.addDateInline}>+ Add Date</button>
                      </div>
                    </div>
                  )}

                  {/* End Date — for all recurrence types */}
                  {form.recurrenceType !== 'custom' && (
                    <div style={{ minWidth: '180px' }}>
                      <label style={styles.label}>Ends on</label>
                      <input style={styles.inputCompact} type="date"
                        value={form.recurrenceEndDate}
                        onChange={function(e) { setForm(function(prev) { return Object.assign({}, prev, { recurrenceEndDate: e.target.value }); }); }}
                        min={form.date}
                        placeholder="End date" />
                    </div>
                  )}
                </div>
              )}

              {/* Row 3: Venue + Max Participants */}
              <div style={s.row2}>
                <div style={styles.fieldFlex2}>
                  <label style={styles.label}>Venue</label>
                  <input
                    style={styles.inputCompact}
                    value={form.venue}
                    onChange={e => setForm({ ...form, venue: e.target.value })}
                  />
                </div>
                <div style={styles.fieldFlexSmall}>
                  <label style={styles.label}>Max Participants</label>
                  <input
                    style={styles.inputCompact}
                    type="number"
                    value={form.maxParticipants}
                    onChange={e => setForm({ ...form, maxParticipants: e.target.value })}
                    placeholder="e.g. 20"
                    required
                  />
                </div>
              </div>

              {/* Description — smaller */}
              <div style={{ marginBottom: '14px' }}>
                <label style={styles.label}>
                  Description — included in confirmation email
                </label>
                <textarea
                  style={{ ...styles.inputCompact, height: '64px', resize: 'vertical' }}
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="Session details, instructions..."
                  required
                />
              </div>

              {/* Eligibility — chip style */}
              <div style={{ marginBottom: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <input
                    type="checkbox"
                    id="openToAll"
                    checked={form.openToAll}
                    onChange={e => setForm({ ...form, openToAll: e.target.checked })}
                  />
                  <label htmlFor="openToAll" style={{ fontSize: '13px', color: '#333', fontFamily: "'Raleway', sans-serif", fontWeight: '600' }}>
                    Open to all students
                  </label>
                </div>
                {!form.openToAll && (
                  <div>
                    <label style={styles.label}>Eligible Courses</label>
                    <div style={s.chipGrid}>
                      {courseList.map(course => {
                        const selected = form.eligibleCourses.includes(course);
                        return (
                          <button
                            key={course}
                            type="button"
                            onClick={() => toggleCourse(course)}
                            style={{
                              ...styles.chip,
                              background: selected ? '#4a8f82' : '#fff',
                              color: selected ? '#fff' : '#555',
                              borderColor: selected ? '#4a8f82' : '#d0d0d0'
                            }}
                          >
                            {selected && '\u2713 '}{course}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div style={s.formBtns}>
                <button type="submit" style={s.submitBtn}>
                  {editingSession ? 'Update Session' : 'Create Session'}
                </button>
                {editingSession && (
                  <button
                    type="button"
                    onClick={() => { setShowForm(false); setEditingSession(null); setForm(emptyForm); }}
                    style={s.cancelBtn}
                  >
                    Cancel Edit
                  </button>
                )}
              </div>
            </form>
          </div>
        )}

        {/* Sessions — Card-based listing */}
        <div style={styles.sectionHeader}>
          <span style={styles.sectionTitle}>All Sessions ({sessions.length})</span>
        </div>

        {loading ? (
          <div style={styles.empty}>Loading sessions...</div>
        ) : sessions.length === 0 ? (
          <div style={styles.empty}>No sessions created yet. Click "Create New Session" to get started.</div>
        ) : (
          <div style={s.cardGrid}>
            {sessions.map(session => (
              <div key={session._id} style={styles.sessionCard}>
                <div style={styles.sessionCardTop}>
                  <div style={{ flex: 1 }}>
                    {/* Clickable title */}
                    <div
                      onClick={() => openDetail(session)}
                      style={styles.sessionTitle}
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => { if (e.key === 'Enter') openDetail(session); }}
                    >
                      {session.title}
                    </div>
                    <div style={styles.sessionMeta}>
                      <span style={styles.typeBadge}>
                        {session.type?.replace(/_/g, ' ')}
                      </span>
                      {session.isRecurring && (
                        <span style={styles.recurBadge}>Recurring</span>
                      )}
                      {session.isRecurringInstance && (
                        <span style={styles.recurBadge}>Instance</span>
                      )}
                    </div>
                  </div>
                  <div style={styles.statusBadgeContainer}>
                    <span style={{
                      ...styles.statusBadge,
                      background: session.status === 'upcoming' ? '#eafaf1' : '#fef9e7',
                      color: session.status === 'upcoming' ? '#27ae60' : '#d4ac0d'
                    }}>
                      {session.status}
                    </span>
                  </div>
                </div>

                <div style={styles.sessionDetails}>
                  <div style={styles.detailItem}>
                    <span style={styles.detailIcon}>&#128197;</span>
                    <span>{formatDate(session.date)}</span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailIcon}>&#128336;</span>
                    <span>{session.startTime} — {session.endTime}</span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailIcon}>&#128205;</span>
                    <span style={{ fontSize: '12px' }}>{session.venue}</span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailIcon}>&#128101;</span>
                    <span>{session.currentEnrollments}/{session.maxParticipants} enrolled</span>
                  </div>
                </div>

                <div style={styles.sessionEligibility}>
                  {session.openToAll ? (
                    <span style={{ fontSize: '11px', color: '#4a8f82' }}>Open to all</span>
                  ) : (
                    <span style={{ fontSize: '11px', color: '#888' }}>
                      {session.eligibleCourses?.length || 0} course(s) eligible
                    </span>
                  )}
                </div>

                <div style={styles.sessionCardFooter}>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {session.announcementEmailSent ? (
                      <span style={styles.sentBadge}>
                        Email Sent ({session.emailRecipientCount})
                      </span>
                    ) : (
                      <button
                        onClick={() => handleSendAnnouncement(session._id, session.title)}
                        style={styles.emailBtn}
                        disabled={sendingEmail === session._id}
                      >
                        {sendingEmail === session._id ? 'Sending...' : 'Send Email'}
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button onClick={() => handleEdit(session)} style={styles.editBtn}>Edit</button>
                    <button onClick={() => handleDelete(session._id)} style={styles.deleteBtn}>Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Detail Modal / Panel */}
        {detailSession && (
          <div style={s.modalOverlay} onClick={closeDetail}>
            <div style={s.modalPanel} onClick={e => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <h2 style={styles.modalTitle}>{detailSession.title}</h2>
                <button onClick={closeDetail} style={styles.modalClose}>&times;</button>
              </div>

              {/* Session Info */}
              <div style={styles.modalInfo}>
                <div style={styles.modalInfoRow}>
                  <span style={styles.modalLabel}>Type</span>
                  <span>{detailSession.type?.replace(/_/g, ' ')}</span>
                </div>
                <div style={styles.modalInfoRow}>
                  <span style={styles.modalLabel}>Date</span>
                  <span>{formatDate(detailSession.date)}</span>
                </div>
                <div style={styles.modalInfoRow}>
                  <span style={styles.modalLabel}>Time</span>
                  <span>{detailSession.startTime} — {detailSession.endTime}</span>
                </div>
                <div style={styles.modalInfoRow}>
                  <span style={styles.modalLabel}>Venue</span>
                  <span>{detailSession.venue}</span>
                </div>
                <div style={styles.modalInfoRow}>
                  <span style={styles.modalLabel}>Capacity</span>
                  <span>{detailSession.currentEnrollments} / {detailSession.maxParticipants}</span>
                </div>
                <div style={styles.modalInfoRow}>
                  <span style={styles.modalLabel}>Eligibility</span>
                  <span>{detailSession.openToAll ? 'Open to all' : (detailSession.eligibleCourses || []).join(', ')}</span>
                </div>
                {detailSession.description && (
                  <div style={{ marginTop: '12px', padding: '10px 12px', background: '#f9faf7', borderRadius: '4px', fontSize: '13px', color: '#555', lineHeight: '1.6' }}>
                    {detailSession.description}
                  </div>
                )}
              </div>

              {/* Tabs: Registered | Eligible Not Registered */}
              <div style={styles.tabRow}>
                <button
                  onClick={() => setDetailTab('registered')}
                  style={{
                    ...styles.tab,
                    borderBottom: detailTab === 'registered' ? '2px solid #4a8f82' : '2px solid transparent',
                    color: detailTab === 'registered' ? '#4a8f82' : '#999'
                  }}
                >
                  Registered ({participants.enrolled.length})
                </button>
                <button
                  onClick={() => setDetailTab('not_registered')}
                  style={{
                    ...styles.tab,
                    borderBottom: detailTab === 'not_registered' ? '2px solid #4a8f82' : '2px solid transparent',
                    color: detailTab === 'not_registered' ? '#4a8f82' : '#999'
                  }}
                >
                  Eligible Not Registered ({participants.eligible_not_enrolled.length})
                </button>
              </div>

              {loadingParticipants ? (
                <div style={{ padding: '24px', textAlign: 'center', color: '#999' }}>Loading participants...</div>
              ) : (
                <div style={styles.participantList}>
                  {detailTab === 'registered' && (
                    participants.enrolled.length === 0 ? (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#999', fontSize: '13px' }}>No registrations yet</div>
                    ) : (
                      participants.enrolled.map((s, i) => (
                        <div key={i} style={styles.participantRow}>
                          <div style={styles.participantName}>{s.firstName} {s.lastName}</div>
                          <div style={styles.participantEmail}>{s.email}</div>
                          {s.phone && <div style={styles.participantPhone}>{s.phone}</div>}
                        </div>
                      ))
                    )
                  )}
                  {detailTab === 'not_registered' && (
                    participants.eligible_not_enrolled.length === 0 ? (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#999', fontSize: '13px' }}>All eligible students have registered</div>
                    ) : (
                      participants.eligible_not_enrolled.map((s, i) => (
                        <div key={i} style={styles.participantRow}>
                          <div style={{ flex: 1 }}>
                            <div style={styles.participantName}>{s.firstName} {s.lastName}</div>
                            <div style={styles.participantEmail}>{s.email}</div>
                            {s.phone && <div style={styles.participantPhone}>{s.phone}</div>}
                          </div>
                          {s.phone && (
                            <a
                              href={buildWhatsAppLink(s, detailSession)}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={styles.whatsappBtn}
                            >
                              WhatsApp
                            </a>
                          )}
                        </div>
                      ))
                    )
                  )}
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

const styles = {
  layout: { display: 'flex', minHeight: '100vh', background: '#f5f8f5', fontFamily: "'Open Sans', sans-serif" },
  main: { marginLeft: '260px', flex: 1, padding: '32px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px' },
  headerTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '26px', fontWeight: '700', color: '#071e12', margin: '0 0 4px' },
  headerSub: { fontSize: '13px', color: '#666', margin: 0 },
  addBtn: { background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '4px', padding: '10px 22px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer' },

  /* Form — Compact */
  formCard: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '24px', marginBottom: '24px' },
  formTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '16px', fontWeight: '700', color: '#071e12', marginBottom: '18px' },
  row2: { display: 'flex', gap: '14px', marginBottom: '14px' },
  row4: { display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' },
  fieldFlex1: { flex: 1, minWidth: '120px' },
  fieldFlex2: { flex: 2, minWidth: '200px' },
  fieldFlexSmall: { flex: '0 0 140px' },
  fieldFlexTime: { flex: '0 0 110px' },
  label: { display: 'block', fontSize: '10px', fontWeight: '700', letterSpacing: '1.2px', textTransform: 'uppercase', color: '#666', marginBottom: '4px', fontFamily: "'Raleway', sans-serif" },
  inputCompact: { width: '100%', padding: '8px 10px', border: '1px solid #e0e0e0', borderRadius: '4px', fontSize: '13px', fontFamily: "'Open Sans', sans-serif", boxSizing: 'border-box', color: '#333', background: '#fff' },
  customDatesRow: { marginBottom: '14px', padding: '10px 12px', background: '#f9faf7', borderRadius: '6px', border: '1px solid #e8ece8' },
  recurrenceOptionsRow: { display: 'flex', gap: '16px', alignItems: 'flex-start', marginBottom: '14px', padding: '12px 14px', background: '#f9faf7', borderRadius: '6px', border: '1px solid #e8ece8' },
  removeSmall: { background: 'none', border: '1px solid #e74c3c', color: '#e74c3c', borderRadius: '3px', padding: '4px 8px', fontSize: '11px', cursor: 'pointer', lineHeight: 1 },
  addDateInline: { background: 'transparent', border: '1px dashed #4a8f82', color: '#4a8f82', borderRadius: '4px', padding: '6px 14px', fontSize: '11px', fontWeight: '700', fontFamily: "'Raleway', sans-serif", cursor: 'pointer' },
  chipGrid: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' },
  chip: { border: '1px solid #d0d0d0', borderRadius: '16px', padding: '5px 12px', fontSize: '11px', fontFamily: "'Open Sans', sans-serif", cursor: 'pointer', background: '#fff', transition: 'all 0.15s', whiteSpace: 'nowrap' },
  formBtns: { display: 'flex', gap: '12px', marginTop: '6px' },
  submitBtn: { background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '4px', padding: '10px 28px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer' },
  cancelBtn: { background: 'transparent', color: '#666', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '10px 20px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', cursor: 'pointer' },

  /* Session cards */
  sectionHeader: { marginBottom: '16px' },
  sectionTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '15px', fontWeight: '700', color: '#071e12' },
  empty: { padding: '40px', textAlign: 'center', color: '#999', fontSize: '14px' },
  cardGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '16px' },
  sessionCard: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '10px' },
  sessionCardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  sessionTitle: { fontFamily: "'Raleway', sans-serif", fontWeight: '700', fontSize: '15px', color: '#4a8f82', cursor: 'pointer', textDecoration: 'underline', textDecorationColor: '#b0d4cc', marginBottom: '4px' },
  sessionMeta: { display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' },
  typeBadge: { fontSize: '10px', fontWeight: '700', letterSpacing: '0.5px', textTransform: 'uppercase', padding: '3px 8px', borderRadius: '10px', background: '#f0f5f4', color: '#4a8f82', fontFamily: "'Raleway', sans-serif" },
  recurBadge: { fontSize: '10px', fontWeight: '700', letterSpacing: '0.5px', textTransform: 'uppercase', padding: '3px 8px', borderRadius: '10px', background: '#eaf4fb', color: '#2980b9', fontFamily: "'Raleway', sans-serif" },
  statusBadgeContainer: { flexShrink: 0, marginLeft: '8px' },
  statusBadge: { fontFamily: "'Raleway', sans-serif", fontSize: '10px', fontWeight: '700', letterSpacing: '0.5px', textTransform: 'uppercase', padding: '4px 10px', borderRadius: '12px' },
  sessionDetails: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', fontSize: '13px', color: '#444' },
  detailItem: { display: 'flex', alignItems: 'center', gap: '6px' },
  detailIcon: { fontSize: '13px', width: '18px', textAlign: 'center', flexShrink: 0 },
  sessionEligibility: { borderTop: '1px solid #f0f0f0', paddingTop: '6px' },
  sessionCardFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f0f0f0', paddingTop: '10px' },
  sentBadge: { fontSize: '10px', fontWeight: '700', letterSpacing: '0.5px', textTransform: 'uppercase', padding: '4px 10px', borderRadius: '10px', background: '#eafaf1', color: '#27ae60', fontFamily: "'Raleway', sans-serif" },
  emailBtn: { background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '4px', padding: '6px 12px', fontSize: '10px', fontWeight: '700', fontFamily: "'Raleway', sans-serif", cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.5px' },
  editBtn: { background: 'none', border: '1px solid #4a8f82', color: '#4a8f82', borderRadius: '4px', padding: '5px 10px', fontSize: '10px', fontWeight: '700', fontFamily: "'Raleway', sans-serif", cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.5px' },
  deleteBtn: { background: 'none', border: '1px solid #e74c3c', color: '#e74c3c', borderRadius: '4px', padding: '5px 10px', fontSize: '10px', fontWeight: '700', fontFamily: "'Raleway', sans-serif", cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.5px' },

  /* Detail Modal */
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(7,30,18,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '60px', zIndex: 1000, overflowY: 'auto' },
  modalPanel: { background: '#fff', borderRadius: '10px', width: '600px', maxWidth: '90vw', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 16px 48px rgba(0,0,0,0.15)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #e0e0e0' },
  modalTitle: { fontFamily: "'Raleway', sans-serif", fontSize: '18px', fontWeight: '700', color: '#071e12', margin: 0 },
  modalClose: { background: 'none', border: 'none', fontSize: '24px', color: '#999', cursor: 'pointer', padding: '0 4px', lineHeight: 1 },
  modalInfo: { padding: '16px 24px', borderBottom: '1px solid #e0e0e0' },
  modalInfoRow: { display: 'flex', gap: '12px', marginBottom: '6px', fontSize: '13px', color: '#444' },
  modalLabel: { fontFamily: "'Raleway', sans-serif", fontWeight: '700', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.8px', color: '#888', minWidth: '80px' },
  tabRow: { display: 'flex', borderBottom: '1px solid #e0e0e0' },
  tab: { flex: 1, background: 'none', border: 'none', padding: '12px 16px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', letterSpacing: '0.5px', textTransform: 'uppercase', cursor: 'pointer', textAlign: 'center' },
  participantList: { overflowY: 'auto', maxHeight: '300px', padding: '0' },
  participantRow: { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 24px', borderBottom: '1px solid #f5f5f5' },
  participantName: { fontFamily: "'Raleway', sans-serif", fontWeight: '700', fontSize: '13px', color: '#071e12' },
  participantEmail: { fontSize: '12px', color: '#888' },
  participantPhone: { fontSize: '11px', color: '#aaa' },
  whatsappBtn: { background: '#25D366', color: '#fff', border: 'none', borderRadius: '4px', padding: '5px 12px', fontSize: '11px', fontWeight: '700', fontFamily: "'Raleway', sans-serif", cursor: 'pointer', textDecoration: 'none', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap', flexShrink: 0 }
};

export default Sessions;
